/**
 * Reminders (spec §9.13): `reminders[id] = {taskId, at, repeat?, firedAt?, for?}`. Any device may fire;
 * `firedAt` (LWW) keeps others from repeating it within 60 s.
 */
import { LoroMap, type LoroDoc } from "loro-crdt";
import { ulid } from "ulid";
import { commitMessage } from "../core/actor";
import { C } from "../core/schema";

export interface Reminder {
  id: string;
  taskId: string;
  at: string;
  repeat?: "daily" | "weekly";
  firedAt?: string | null;
  for?: string;
}

export function listReminders(doc: LoroDoc): Reminder[] {
  const all = doc.getMap(C.reminders).toJSON() as Record<string, Omit<Reminder, "id">>;
  return Object.entries(all)
    .filter(([, r]) => r && typeof r === "object" && typeof r.at === "string")
    .map(([id, r]) => ({ ...r, id }));
}

export function addReminder(
  doc: LoroDoc,
  taskId: string,
  at: Date,
  repeat?: Reminder["repeat"],
  forProfile?: string | null,
): string {
  const id = ulid();
  const m = doc.getMap(C.reminders).setContainer(id, new LoroMap());
  m.set("taskId", taskId);
  m.set("at", at.toISOString());
  if (repeat) m.set("repeat", repeat);
  if (forProfile) m.set("for", forProfile);
  m.set("firedAt", null);
  doc.commit({ message: commitMessage({ op: "addReminder" }) });
  return id;
}

export function removeReminder(doc: LoroDoc, id: string): void {
  doc.getMap(C.reminders).delete(id);
  doc.commit({ message: commitMessage({ op: "removeReminder" }) });
}

/** Reminders that are due and have not fired (on any device) for this occurrence. */
export function dueReminders(reminders: Reminder[], nowMs: number, me?: string | null): Reminder[] {
  return reminders.filter((r) => {
    if (r.for && me && r.for !== me) return false; // someone else's reminder (spec §9.29)
    const at = Date.parse(r.at);
    if (Number.isNaN(at) || at > nowMs) return false;
    return !r.firedAt || Date.parse(r.firedAt) < at;
  });
}

/** Mark as fired; a repeating reminder moves its `at` forward instead of staying fired forever. */
export function markFired(doc: LoroDoc, r: Reminder, now = new Date()): void {
  const m = doc.getMap(C.reminders).get(r.id) as LoroMap | undefined;
  if (!m) return;
  m.set("firedAt", now.toISOString());
  if (r.repeat) {
    const step = r.repeat === "daily" ? 86_400_000 : 7 * 86_400_000;
    let next = Date.parse(r.at);
    while (next <= now.getTime()) next += step;
    m.set("at", new Date(next).toISOString());
  }
  doc.commit({ message: commitMessage({ op: "reminderFired" }) });
}
